import IntlPhoneInput from "@components/intl-phone-input";
import { Modal } from "@components/Modal/Modal";
import useRecaptcha from "@hooks/useRecaptcha";
import useVercelMeta from "@hooks/useVercelMeta";
import * as Sentry from "@sentry/nextjs";
import { trackEvent } from "@utils/amplitudeTracker";
import { verifyPhoneNumber, windowOpenNewTab } from "@utils/common";
import { validateEmail } from "@utils/validations";
import axios from "axios";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { HiX } from "react-icons/hi";
import { LuArrowUpRight } from "react-icons/lu";
import OtpInput from "react-otp-input";

interface AffiliateSignupModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Step = "form" | "otp" | "success";

const ROLE_OPTIONS = ["Digital Marketer", "Social Media Marketer", "Youtuber", "Affiliate Marketer", "Influencer", "Content Creator", "Podcaster", "Other"];

const siteKey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY || "";
const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ComponentType<any>;

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    password: string;
    gstVatPanTaxId: string;
    howToPromote: string;
}

const initialFormData: FormData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "Digital Marketer",
    password: "",
    gstVatPanTaxId: "",
    howToPromote: "",
};

const AffiliateSignupModal: React.FC<AffiliateSignupModalProps> = ({ isOpen, onClose }) => {
    const [step, setStep] = useState<Step>("form");
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [otp, setOtp] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [otpResendTimer, setOtpResendTimer] = useState(0);
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const { country: countryIso = "IN" } = useVercelMeta();
    // React state updates are async; use a ref to synchronously prevent duplicate OTP sends/resends.
    const otpRequestInFlightRef = useRef(false);

    const { reCaptchaRef, triggerReCaptcha, generateToken } = useRecaptcha(async (token: string) => {
        await sendInviteWithToken(token);
    });

    useEffect(() => {
        if (otpResendTimer > 0) {
            const timer = setTimeout(() => setOtpResendTimer(otpResendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpResendTimer]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setStep("form");
            setFormData(initialFormData);
            setOtp("");
            setError(null);
            setErrors({});
            setIsLoading(false);
            otpRequestInFlightRef.current = false;
        }
    }, [isOpen]);

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<FormData> = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = "First name is required";
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = "Last name is required";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!validateEmail(formData.email)) {
            newErrors.email = "Please enter a valid email";
        }

        if (verifyPhoneNumber(formData.phone)) {
            newErrors.phone = "Please enter a valid phone number";
        }

        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 8) {
            newErrors.password = "Password must be at least 8 characters";
        }

        if (!formData.gstVatPanTaxId.trim()) {
            newErrors.gstVatPanTaxId = "GST / VAT / PAN / TAX ID is required";
        }

        if (!formData.howToPromote.trim()) {
            newErrors.howToPromote = "How do you plan to promote our product? is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendOtp = async () => {
        if (!validateForm()) return;
        if (otpRequestInFlightRef.current) return;
        otpRequestInFlightRef.current = true;

        setIsLoading(true);
        setError(null);
        try {
            triggerReCaptcha();
        } catch (err) {
            otpRequestInFlightRef.current = false;
            setIsLoading(false);
            Sentry.captureException(err);
            setError("Failed to initiate OTP. Please try again.");
        }
    };

    const sendInviteWithToken = async (recaptchaToken: string) => {
        try {
            await axios.post("/api/v1/affiliates/invite", {
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                promotionPlan: formData.howToPromote,
                taxId: formData.gstVatPanTaxId,
                profession: formData.role,
                recaptchaToken,
            });

            setStep("otp");
            setOtpResendTimer(60);
            trackEvent({ event: "JOIN AFFILIATE PROGRAM - OTP SENT", properties: { email: formData.email } });
        } catch (err) {
            Sentry.captureException(err);
            const errorMessage = (err as any).response?.data?.error;
            setError(errorMessage || "Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
            otpRequestInFlightRef.current = false;
        }
    };

    const handleOtpChange = (value: string) => {
        setOtp(value);
    };

    const handleVerifyOtp = async () => {
        const otpValue = otp;
        if (otpValue.length !== 6) {
            setError("Please enter the complete OTP");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await axios.post("/api/v1/affiliates/verify-otp", {
                email: formData.email,
                otp: otpValue,
            });

            trackEvent({ event: "JOIN AFFILIATE PROGRAM - REGISTERED", properties: { email: formData.email, name: `${formData.firstName} ${formData.lastName}` } });

            windowOpenNewTab("https://gallabox.tapfiliate.com/login");
            setStep("success");
        } catch (err) {
            Sentry.captureException(err);
            const errorMessage = (err as any).response?.data?.error;
            setError(errorMessage || "Failed to register affiliate. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (otpResendTimer > 0) return;
        if (otpRequestInFlightRef.current) return;
        otpRequestInFlightRef.current = true;
        setIsLoading(true);
        setOtp("");
        setError(null);
        try {
            triggerReCaptcha();
        } catch (err) {
            otpRequestInFlightRef.current = false;
            setIsLoading(false);
            Sentry.captureException(err);
            setError("Failed to resend OTP. Please try again.");
        }
    };

    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    const isBrowser = typeof window !== "undefined";
    if (!isBrowser) return null;

    return (
        <Modal>
            <div className="fixed z-100 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-800 bg-opacity-80 transition-opacity" aria-hidden="true" onClick={handleClose}></div>

                    <div className="relative inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-2xl mx-4">
                        <button
                            data-cy="affiliate-modal-close-btn"
                            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full z-10"
                            onClick={handleClose}
                        >
                            <HiX size={20} />
                        </button>

                        <div className="p-6 sm:p-8">
                            {step === "form" && (
                                <SignupForm
                                    formData={formData}
                                    errors={errors}
                                    country={countryIso}
                                    showPassword={showPassword}
                                    isLoading={isLoading}
                                    error={error}
                                    onInputChange={handleInputChange}
                                    onPhoneChange={(value) => handleInputChange("phone", value)}
                                    onTogglePassword={() => setShowPassword(!showPassword)}
                                    onSubmit={handleSendOtp}
                                />
                            )}

                            {step === "otp" && (
                                <OtpVerification
                                    email={formData.email}
                                    otp={otp}
                                    isLoading={isLoading}
                                    error={error}
                                    resendTimer={otpResendTimer}
                                    onOtpChange={handleOtpChange}
                                    onVerify={handleVerifyOtp}
                                    onResend={handleResendOtp}
                                    onBack={() => setStep("form")}
                                />
                            )}

                            {step === "success" && <OtpVerified email={formData.email} onClose={handleClose} />}

                            <ReCAPTCHAComponent ref={reCaptchaRef} sitekey={siteKey} size="invisible" badge="bottomright" onChange={generateToken} />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

interface SignupFormProps {
    formData: FormData;
    errors: Partial<FormData>;
    country: string;
    showPassword: boolean;
    isLoading: boolean;
    error: string | null;
    onInputChange: (field: keyof FormData, value: string) => void;
    onPhoneChange: (value: string) => void;
    onTogglePassword: () => void;
    onSubmit: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ formData, errors, country, isLoading, error, onInputChange, onPhoneChange, onSubmit }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
    };

    return (
        <form onSubmit={handleSubmit}>
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Join Our Affiliate Program Today!</h2>

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">{error}</div>}

            <div className="space-y-4">
                <div className="grid md:grid-cols-2  md:gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            First name<span className="text-red-500">*</span>
                        </label>
                        <input
                            data-cy="affiliate-first-name-input"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => onInputChange("firstName", e.target.value)}
                            placeholder="Name"
                            disabled={isLoading}
                            className={classNames(
                                "w-full h-12 px-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                errors.firstName ? "border-red-500 bg-red-50" : "border-gray-300"
                            )}
                            style={{ marginBottom: "0" }}
                        />
                        {errors.firstName && <p className="text-red-500 text-xs my-1">{errors.firstName}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last name<span className="text-red-500">*</span>
                        </label>
                        <input
                            data-cy="affiliate-last-name-input"
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => onInputChange("lastName", e.target.value)}
                            placeholder="Name"
                            disabled={isLoading}
                            className={classNames(
                                "w-full h-12 px-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                errors.lastName ? "border-red-500 bg-red-50" : "border-gray-300"
                            )}
                            style={{ marginBottom: "0" }}
                        />
                        {errors.lastName && <p className="text-red-500 text-xs my-1">{errors.lastName}</p>}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone<span className="text-red-500">*</span>
                    </label>
                    <IntlPhoneInput
                        data-cy="affiliate-phone-input"
                        country={country}
                        placeholder="Enter your mobile number"
                        className={classNames(
                            "mb-0 w-full h-12 pl-16 pr-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                            errors.phone ? "border-red-500 bg-red-50" : "border-gray-300"
                        )}
                        onChange={onPhoneChange}
                        disabled={isLoading}
                        inputStyle={{ marginBottom: "0" }}
                    />
                    {errors.phone && <p className="text-red-500 text-xs my-1">{errors.phone}</p>}
                </div>

                <div className="grid md:grid-cols-2  md:gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            How do you plan to promote our product?<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                data-cy="affiliate-how-to-promote-input"
                                type="text"
                                value={formData.howToPromote}
                                onChange={(e) => onInputChange("howToPromote", e.target.value)}
                                disabled={isLoading}
                                className={classNames(
                                    "w-full h-12 px-4 pr-12 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                    errors.howToPromote ? "border-red-500 bg-red-50" : "border-gray-300"
                                )}
                                style={{ marginBottom: "0" }}
                            />
                        </div>
                        {errors.howToPromote && <p className="text-red-500 text-xs my-1">{errors.howToPromote}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            GST / VAT / PAN / TAX ID<span className="text-red-500">*</span>
                        </label>
                        <input
                            data-cy="affiliate-tax-id-input"
                            type="text"
                            value={formData.gstVatPanTaxId}
                            onChange={(e) => onInputChange("gstVatPanTaxId", e.target.value)}
                            disabled={isLoading}
                            className={classNames(
                                "w-full h-12 px-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                errors.gstVatPanTaxId ? "border-red-500 bg-red-50" : "border-gray-300"
                            )}
                            style={{ marginBottom: "0" }}
                        />
                        {errors.gstVatPanTaxId && <p className="text-red-500 text-xs my-1">{errors.gstVatPanTaxId}</p>}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Which of the following best describes what you do?</label>
                    <div className="relative">
                        <select
                            data-cy="affiliate-role-select"
                            value={formData.role}
                            onChange={(e) => onInputChange("role", e.target.value)}
                            disabled={isLoading}
                            className="w-full h-12 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                            style={{ marginBottom: "0" }}
                        >
                            {ROLE_OPTIONS.map((role) => (
                                <option key={role} value={role}>
                                    {role}
                                </option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email<span className="text-red-500">*</span>
                    </label>
                    <input
                        data-cy="affiliate-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => onInputChange("email", e.target.value)}
                        placeholder="Enter your work email"
                        disabled={isLoading}
                        className={classNames(
                            "w-full h-12 px-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                            errors.email ? "border-red-500 bg-red-50" : "border-gray-300"
                        )}
                        style={{ marginBottom: "0" }}
                    />
                    {errors.email && <p className="text-red-500 text-xs my-1">{errors.email}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password<span className="text-red-500">*</span>
                    </label>
                    <input
                        data-cy="affiliate-password-input"
                        type="text"
                        value={formData.password}
                        onChange={(e) => onInputChange("password", e.target.value)}
                        placeholder="Enter password for your account"
                        disabled={isLoading}
                        className={classNames(
                            "w-full h-12 px-4 border rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                            errors.password ? "border-red-500 bg-red-50" : "border-gray-300"
                        )}
                        style={{ marginBottom: "0" }}
                    />
                    {errors.password && <p className="text-red-500 text-xs my-1">{errors.password}</p>}
                </div>

                <button
                    data-cy="affiliate-signup-btn"
                    type="submit"
                    disabled={isLoading}
                    className={classNames(
                        "w-full h-12 bg-blue-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors",
                        isLoading && "opacity-70 cursor-not-allowed"
                    )}
                >
                    {isLoading ? (
                        "Sending OTP..."
                    ) : (
                        <>
                            Signup & Become Partner
                            <LuArrowUpRight size={18} />
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

interface OtpVerificationProps {
    email: string;
    otp: string;
    isLoading: boolean;
    error: string | null;
    resendTimer: number;
    onOtpChange: (value: string) => void;
    onVerify: () => void;
    onResend: () => void;
    onBack: () => void;
}

interface OtpVerifiedProps {
    email: string;
    onClose: () => void;
}

const OtpVerified: React.FC<OtpVerifiedProps> = ({ email, onClose }) => {
    return (
        <div className="space-y-6 mx-auto text-center" style={{ maxWidth: "410px" }}>
            <div className="flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
            </div>

            <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">OTP Verified!</h2>
                <p className="text-gray-600">
                    Your registration is complete. Please check your email <br />
                    <span className="font-medium">{email}</span>
                </p>
            </div>
            <button
                data-cy="affiliate-success-close-btn"
                onClick={onClose}
                className="w-full h-12 bg-blue-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
            >
                Close
            </button>
        </div>
    );
};

const OtpVerification: React.FC<OtpVerificationProps> = ({ email, otp, isLoading, error, resendTimer, onOtpChange, onVerify, onResend }) => {
    return (
        <div className="space-y-6 mx-auto" style={{ maxWidth: "410px" }}>
            <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Verification</h2>
                <p className="text-gray-600">
                    A 6digit OTP has been sent to <br />
                    <span className="font-medium">{email}</span>
                </p>
            </div>

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">{error}</div>}
            <p className="text-gray-600 text-sm text-center">Enter the 6 digit OTP</p>
            <div>
                <OtpInput
                    value={otp}
                    onChange={onOtpChange}
                    numInputs={6}
                    renderSeparator={<span className="md:mx-2 mx-1"></span>}
                    renderInput={(props) => (
                        <input
                            {...props}
                            data-cy="affiliate-otp-input"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            disabled={isLoading}
                            className="text-center h-12 text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            style={{ padding: 0 }}
                        />
                    )}
                    shouldAutoFocus={true}
                    inputType="number"
                />
            </div>
            <div className="text-center">
                {resendTimer > 0 ? (
                    <p className="text-gray-500 text-sm">Resend OTP in {resendTimer}s</p>
                ) : (
                    <button data-cy="affiliate-resend-otp-btn" onClick={onResend} disabled={isLoading} className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                        Resend OTP
                    </button>
                )}
            </div>
            <button
                data-cy="affiliate-verify-btn"
                onClick={onVerify}
                disabled={isLoading || otp.length !== 6}
                className={classNames(
                    "w-full h-12 bg-blue-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors",
                    (isLoading || otp.length !== 6) && "opacity-70 cursor-not-allowed"
                )}
            >
                {isLoading ? "Verifying..." : "Verify"}
            </button>
        </div>
    );
};

export default AffiliateSignupModal;
